import { useCallback, useContext, useRef } from 'react';

import { authRefreshIdToken } from 'PrivateApis';

import { UserContext } from 'Context/user';
import { useRemoveUser } from 'Context/useRemoveUser';

import { REFRESH_TOKENS } from 'Utilities/constants';

import { ERROR_TYPES, isCognitoErrorCheck } from '../helpers/cognitoErrors';

export const useRefreshIdToken = () => {
  const { userActions: { update: updateUser }, userState } = useContext(UserContext);
  const errorStatus = useRef<number | null>(null);
  const numberOfConsecutiveFailedAttempts = useRef<number>(0);

  const { removeUser } = useRemoveUser();

  const updateStatusCB = useCallback((status?: number) => {
    errorStatus.current = status ?? null;
  }, []);

  const attemptTokenRefresh = useCallback(async () => {
    if (!userState?.cognitoTokens?.refreshToken) {
      window?.Sentry?.captureException?.(new Error('No refresh token found'), {
        extra: {
          tag: 'refresh cognito id token',
        },
      });
      return;
    }

    const { error: fetchError, ...authRefreshTokenResponse } = await authRefreshIdToken({
      refreshToken: userState?.cognitoTokens?.refreshToken ?? '',
    }, updateStatusCB).catch((err) => ({ error: err }));

    if (authRefreshTokenResponse?.idToken) {
      numberOfConsecutiveFailedAttempts.current = 0;
      updateUser({
        cognitoTokens: {
          ...userState?.cognitoTokens,
          ...authRefreshTokenResponse,
        },
      });
      return;
    }

    const isCognitoError = isCognitoErrorCheck(errorStatus.current);
    const errorType = isCognitoError ? ERROR_TYPES.COGNITO : ERROR_TYPES.GENERIC;

    window?.Sentry?.captureException?.(new Error(fetchError), {
      extra: {
        errorType,
        tag: 'refresh cognito id token',
      },
      level: 'log',
    });

    numberOfConsecutiveFailedAttempts.current += 1;
    const isFailedMaxAttempts = numberOfConsecutiveFailedAttempts.current === REFRESH_TOKENS.MAX_RETRIES;

    if (isCognitoError || isFailedMaxAttempts) {
      removeUser(true);
      return;
    }
    attemptTokenRefresh();
  }, [removeUser, updateStatusCB, updateUser, userState?.cognitoTokens]);

  return {
    attemptTokenRefresh,
  };
};
